import { useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Stack, TextInput, Button, Text, tokens } from '@hotjar/hotsauce';
import { ColFormWrapper, Form } from '@hotjar/webapp/features/Registration/common/styles';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useOnboardingNavigation } from '../hooks/useOnboardingNavigation';

interface PhoneNumberStepProps {
  onSubmit: (phoneNumber?: string) => void;
  userId: number;
  isSubmitInProgress?: boolean;
}

const FormWrapper = styled(Form)`
  max-width: 465px;
  margin: 0 auto;
`;

const ButtonWrapper = styled.div`
  display: flex;
`;

const ONBOARDING_PHONE_NUMBER_STEP_UI_POSITION = 'onboarding-phone-number';

export const MEDIUM_OR_LARGE_COMPANY_SIZE_MIN = 101;

export const PhoneNumberStep = ({
  onSubmit,
  userId,
  isSubmitInProgress,
}: PhoneNumberStepProps): JSX.Element => {
  const { t } = useTranslation();
  const { getOrgDetails } = useOnboardingNavigation();
  const [phoneNumber, setPhoneNumber] = useState(getOrgDetails(userId)?.phoneNumber || '');
  const { click: trackClick } = useTrack();

  const [isSkipInProgress, setIsSkipInProgress] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    trackClick(phoneNumber, ONBOARDING_PHONE_NUMBER_STEP_UI_POSITION, {});
    trackClick('Phone number submitted', 'Next - Phone number Submitted', {});
    onSubmit(phoneNumber);
  };

  const handleSkip = () => {
    trackClick('skip', ONBOARDING_PHONE_NUMBER_STEP_UI_POSITION, {});
    setIsSkipInProgress(true);
    onSubmit();
  };

  return (
    <ColFormWrapper data-testid="organization-steps-phone-number">
      <FormWrapper onSubmit={handleSubmit} noValidate>
        <Text
          as="h1"
          textType="header4"
          css={{ marginBottom: tokens.spacer.x1 }}
          textColor="text_default"
        >
          {t('activation:onboarding.organization-steps.phone-number.title')}
        </Text>
        <Text
          as="p"
          textType="body2"
          textColor="text_light"
          css={{ marginBottom: tokens.spacer.x2 }}
        >
          {t('activation:onboarding.organization-steps.phone-number.subtitle')}
        </Text>
        <Stack preset="form">
          <TextInput
            size="l"
            id="phoneNumber"
            name="phoneNumber"
            autoFocus
            isRequired
            maxLength={31}
            placeholder={t('activation:onboarding.organization-steps.phone-number.placeholder')}
            data-testid="organization-phone-number"
            aria-label={t('activation:onboarding.organization-steps.phone-number.aria-label')}
            defaultValue={getOrgDetails(userId)?.phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
          <ButtonWrapper>
            <Button
              css={{ marginRight: tokens.spacer.x2 }}
              size="l"
              type="submit"
              id="submit-step-phone-number"
              data-testid="submit-step-phone-number"
              isLoading={isSubmitInProgress && !isSkipInProgress}
              onClick={handleSubmit}
              isDisabled={!phoneNumber || isSkipInProgress}
              loadingText={t('common:registration.select-signup-method.submit-btn-loading')}
            >
              {t('activation:onboarding.organization-steps.submit-cta')}
            </Button>
            <Button
              size="l"
              kind="secondary"
              id="skip-step-phone-number"
              data-testid="skip-step-phone-number"
              isLoading={isSkipInProgress}
              onClick={handleSkip}
              loadingText={t('common:registration.select-signup-method.submit-btn-loading')}
            >
              {t('activation:onboarding.organization-steps.skip-cta')}
            </Button>
          </ButtonWrapper>
        </Stack>
      </FormWrapper>
    </ColFormWrapper>
  );
};
